import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { BookOpen, TrendingDown, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';

export default function ProblemSection() {
  const { t, language } = useLanguage();

  const problems = [
    {
      icon: BookOpen,
      title: t.problem.point1Title,
      desc: t.problem.point1Desc,
      color: '#EA5658',
    },
    {
      icon: TrendingDown,
      title: t.problem.point2Title,
      desc: t.problem.point2Desc,
      color: '#F8CD46',
    },
    {
      icon: Building2,
      title: t.problem.point3Title,
      desc: t.problem.point3Desc,
      color: '#926EF7',
    },
  ];

  return (
    <section
      data-testid="problem-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-[#EA5658] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.problem.overline}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#14274B] leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.problem.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-[#475569] leading-relaxed mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.problem.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-[#475569] leading-relaxed"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'En France, les cours d\'anglais proposés dans le système scolaire classique ne suffisent pas pour atteindre un vrai bilinguisme académique. Les enfants de retour d\'expatriation perdent rapidement leur niveau, et les écoles internationales restent souvent inaccessibles en raison des listes d\'attente et des coûts élevés. OFA France apporte une solution concrète à ces défis.'
              : 'In France, the English classes offered in the standard school system are not enough to achieve true academic bilingualism. Children returning from abroad quickly lose their level, and international schools often remain inaccessible due to waiting lists and high costs. OFA France provides a concrete solution to these challenges.'}
          </motion.p>
        </div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              data-testid={`problem-card-${index}`}
              className="bg-[#E7EAEF] p-8"
            >
              <div
                className="w-14 h-14 flex items-center justify-center mb-6"
                style={{ backgroundColor: `${problem.color}15` }}
              >
                <problem.icon
                  className="w-7 h-7"
                  style={{ color: problem.color }}
                />
              </div>

              <h3
                className="text-xl md:text-2xl font-semibold text-[#14274B] mb-3"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {problem.title}
              </h3>

              <p
                className="text-[#475569] leading-relaxed"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {problem.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
